import type { ValidationConfig } from "@/components/units/MediaUploader/mediaStore";

import type { BrandId } from "../brands";
import type { Area, BaseEntityLocation } from "../locations";

export interface IWorkDayHours {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

export interface IOpeningHours {
  schedules: IWorkDayHours[];
  timezone?: string;
}

export type GoogplePlaceId = string;

export interface MediaVariant {
  url: string;
  type: string;
  variant: string;
}

export interface MediaItem {
  id: string;
  mediaId: string;
  scope: string;
  variants: MediaVariant[];
  createdAt: string;
  updatedAt: string;
  mediaName: string;
  type: string;
  order: number;
}

export interface IProperty {
  name: string;
  id: string;
  type: string;
}

export interface IBranch {
  nameEn: string;
  nameAr: string;
  descriptionEn?: string;
  descriptionAr?: string;
  brandId?: BrandId;
  phone?: string | null;
  smsPhoneNumbers?: string[] | null;
  addressEn: string;
  addressAr: string;
  openingHours: IOpeningHours;
  reservationRequired?: boolean;
  reservationMethods: string[];
  averageWaitTime: string;
  priceLevel?: number;
  media?: string[];
  reels?: string[];
  legalEntityId?: string | null | undefined;
  legalEntityName?: string | undefined;
  googlePlaceId?: GoogplePlaceId | null;
  menuUrl?: string | null;
  bookingUrl?: string | null;
  active?: boolean;
  mediaItems?: MediaItem[];
  properties?: IProperty[];
  longitude?: number | null;
  latitude?: number | null;
  googlePlaceAverageRating?: number | null;
  googleTotalReviews?: number | null;
  groupId?: string;
  groupName?: string;
  brandName?: string;
  reviewSummaryEn?: string;
  reviewSummaryAr?: string;
  area: Area;
  street: BaseEntityLocation;
  landmark: BaseEntityLocation;
  cityId: string;
  city?: {
    id: string;
    nameEn: string;
    nameAr: string;
    countryCode: string;
    timezone: string;
    coordinates?: string | null;
    createdAt: string;
    updatedAt: string;
  };
  cashback: number;
  isTestBranch: boolean;
  tables?: string;
  tablesQrCode?: string | null;
  serviceTypes?: string[];
  serviceType?: string[] | null;
  operations?: string;
  ordersOperationType?: string | null;
  isPosIntegrated?: boolean;
  isScannerEnabled?: boolean;
  posClientId?: string | null;
  posBranchId?: string | null;
  tableNumberLength?: string | number | null;
  posName?: string | null;
  branchReceiptBillNumberMedia?: MediaItem | null;
  branchReceiptBillNumberMediaItemId?: string | null;
  branchReceiptOrderTotalMedia?: MediaItem | null;
  branchReceiptOrderTotalMediaItemId?: string | null;
  brandActive?: string;
  brandCountry?: string;
  brandCurrency?: string;
}

export type BranchId = string;

export interface IBranchFull extends IBranch {
  id: BranchId;
  referenceNumber?: number | null;
}

export interface IBranchInfo {
  id: BranchId;
  referenceNumber?: string;
  phone: string;
  address: string;
  name: string;
  active: boolean;
  legalEntityName?: string;
  legalEntityId?: string;
  createdAt?: string;
}

export const BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES = {
  "image/jpeg": [".jpeg", ".jpg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
};

export const BRANCH_MEDIA_ACCEPTED_VIDEO_TYPES = {
  "video/mp4": [".mp4"],
};

export const BRANCH_MEDIA_ACCEPTED_TYPES = {
  ...BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES,
  ...BRANCH_MEDIA_ACCEPTED_VIDEO_TYPES,
};

export const RECEIPT_MEDIA_VALIDATION: ValidationConfig = {
  image: {
    acceptedMimeTypes: Object.keys(BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES),
    maxSize: 3 * 1024 * 1024,
    dimensionConstraints: { minWidth: 700, minHeight: 700 },
  },
  limits: { maxTotal: 1 },
};

export const BranchMediaValidation: ValidationConfig = {
  image: {
    acceptedMimeTypes: Object.keys(BRANCH_MEDIA_ACCEPTED_IMAGE_TYPES),
    maxSize: 3 * 1024 * 1024,
    minSize: 1000,
    dimensionConstraints: {
      minWidth: 700,
      minHeight: 700,
    },
  },
  video: {
    acceptedMimeTypes: Object.keys(BRANCH_MEDIA_ACCEPTED_VIDEO_TYPES),
    maxSize: 100 * 1024 * 1024,
    dimensionConstraints: {
      minWidth: 1920,
      minHeight: 1080,
    },
  },
  limits: {
    maxTotal: 10,
  },
};

export type IBranchPaylad = Omit<IBranch, "properties"> & {
  properties?: string[];
};
